/**
 * The wordmark: NEXIAL / VENTURES, set so that its X's ink sits exactly on the
 * star, so the flare is the letter. The type is measured on the real face
 * every frame. At the supernova the letters move apart from the X, the intro's
 * drawing-together run backwards and pushed by the blast, and fade.
 */
import { keyCurve, clamp, smoothstep } from '../easing'
import type { Viewport } from '../viewport'
import { IMPLODE, SQUARE_ARMS, type StarArm } from './star'
import { T_DET } from './supernova'

/* =====================================================================
   TWEAKABLES. Times are seconds.
   ===================================================================== */
const X_GLYPH = 0.0 /* the letter X under the flare: 0 = only the flare, 1 = as bright as the other letters */
/* the word's fade-in, as keyframes: at each time the word has that opacity, and
   a smooth curve runs through them (it never overshoots or dips back) */
const WORD_IN_START = 0.8 /* s: when the fade starts; the times below count from here */
const WORD_FADE_T = [0, 0.3, 0.85] /* s after WORD_IN_START ... */
const WORD_FADE_V = [0, 0.5, 1] /* ...and the opacity at each (0 = hidden, 1 = full) */
const SUB_IN: [number, number] = [1.25, 2.0] /* VENTURES */
const WORD_BLUR = 8 /* px of blur it softens to as it leaves at the supernova (0 = none) */
const WORD_EXIT = 1.0 /* s: at the supernova the letters move apart from the X and fade */
const WORD_SPREAD = 0.9 /* ...how much letter-spacing they gain (em) */
/* ===================================================================== */

export interface Wordmark {
  /** Measures the X's ink on the real face, and the length of its arm (centre to corner, px) at the current type size. */
  measure(arms: readonly StarArm[]): void
  /** The X's arm, centre to corner, in px at the current type size: how far the star's spikes reach. */
  armLength(): number
  /** Sets the wordmark for time `t`: its opacity, spacing and blur, with its X on the star. */
  update(vp: Viewport, t: number): void
  /** Hides it. */
  hide(): void
}

function element(id: string): HTMLElement {
  return document.getElementById(id)!
}

export function createWordmark(): Wordmark {
  const word = element('word')
  const mainEl = word.querySelector<HTMLElement>('.main')!
  const xg = element('xg')
  const sub = element('wSub')
  const baseline = element('bl')
  /* the X's ink, as fractions of the font size: how far its centre sits above the baseline, and its height */
  let xMid = 0.36
  let xCapHeight = 0.72
  let arm = 40

  return {
    measure(arms) {
      measureInk()
      measureArm(arms)
    },
    armLength: () => arm,
    update(vp, t) {
      const fadeIn = clamp(keyCurve(WORD_FADE_T, WORD_FADE_V, t - WORD_IN_START), 0, 1)
      const tau = t - T_DET
      /* the exit: fast at first, as if pushed, then easing, and gone */
      const u = clamp(tau / WORD_EXIT, 0, 1)
      const push = 1 - Math.pow(1 - u, 3)
      const alpha = fadeIn * (1 - smoothstep(0, 0.75, u))
      if (alpha < 0.002) {
        word.style.opacity = '0'
        return
      }
      const spacing = 0.25 + WORD_SPREAD * push + 'em'
      mainEl.style.letterSpacing = spacing
      mainEl.style.textIndent = spacing
      const subSpacing = 0.42 + WORD_SPREAD * 0.8 * push + 'em'
      sub.style.opacity = smoothstep(SUB_IN[0], SUB_IN[1], t).toFixed(3)
      sub.style.letterSpacing = subSpacing
      sub.style.textIndent = subSpacing
      /* keep the X's ink on the star */
      word.style.transform = 'none'
      const glyph = xg.getBoundingClientRect()
      const letterSpace = parseFloat(getComputedStyle(mainEl).letterSpacing) || 0
      const fontSize = parseFloat(getComputedStyle(mainEl).fontSize) || 0
      /* horizontally the glyph's advance, less its trailing letter-space; vertically
         the real baseline (a zero-size marker sitting on it) less the ink's half-height */
      const gx = glyph.left + (glyph.width - letterSpace) / 2
      const gy = baseline.getBoundingClientRect().bottom - xMid * fontSize
      word.style.transform =
        'translate(' + (vp.cx - gx).toFixed(1) + 'px, ' + (vp.cy - gy).toFixed(1) + 'px)'
      /* softening as it leaves */
      word.style.filter =
        WORD_BLUR > 0 && push > 0.005 ? 'blur(' + (WORD_BLUR * 0.5 * push).toFixed(2) + 'px)' : ''
      word.style.opacity = alpha.toFixed(3)
      /* the faint X under the flare is drawn in with the implosion */
      xg.style.setProperty(
        '--xa',
        (X_GLYPH * fadeIn * (1 - smoothstep(IMPLODE[0], IMPLODE[1], t))).toFixed(3)
      )
    },
    hide() {
      word.style.opacity = '0'
      word.setAttribute('aria-hidden', 'true')
    },
  }

  function measureArm(arms: readonly StarArm[]) {
    const fontSize = parseFloat(getComputedStyle(mainEl).fontSize) || 60
    const angle = (arms[1] ?? SQUARE_ARMS[1]).angle
    arm = (fontSize * xCapHeight) / 2 / Math.max(Math.abs(Math.sin(angle)), 0.3)
  }

  function measureInk() {
    const g = document.createElement('canvas').getContext('2d')!
    g.font = "200 100px Manrope, 'Helvetica Neue', Helvetica, Arial, sans-serif"
    const m = g.measureText('X')
    if (m.actualBoundingBoxAscent) {
      xCapHeight = (m.actualBoundingBoxAscent + (m.actualBoundingBoxDescent || 0)) / 100
      xMid = (m.actualBoundingBoxAscent - (m.actualBoundingBoxDescent || 0)) / 2 / 100
    }
  }
}
